import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../api';

const CURR_SYM = { USD:'$',EUR:'€',JPY:'¥',CNY:'¥',KRW:'₩',RUB:'₽' };
const FLAGS = { JP:'🇯🇵',ES:'🇪🇸',FR:'🇫🇷',CN:'🇨🇳',US:'🇺🇸',KR:'🇰🇷',IT:'🇮🇹',RU:'🇷🇺' };

export default function CartPage() {
  const [items, setItems] = useState([]);
  const [shippingInfo, setShippingInfo] = useState({});
  const [address, setAddress] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    const r = await API.get('/market/cart');
    setItems(r.data);
    // 获取每件商品的运费
    if (r.data.length > 0) {
      const info = {};
      for (const item of r.data) {
        if (!item.product) continue;
        try {
          const s = await API.get(`/market/shipping?fromCountry=${item.product.country_code}&toCountry=${user?.country_code || 'CN'}`);
          info[item.product_id] = s.data;
        } catch {}
      }
      setShippingInfo(info);
    }
  };

  const removeItem = async (pid) => {
    await axios.delete(`/api/market/cart/${pid}`);
    setItems(prev => prev.filter(i => i.product_id !== pid));
  };

  const checkout = async () => {
    if (!address.trim()) return setMsg('请填写收货地址');
    setLoading(true);
    try {
      const r = await API.post('/market/checkout', { shippingAddress: address });
      if (r.data.url) {
        window.location.href = r.data.url;
        return;
      }
      setMsg(r.data.message);
      setItems([]);
      setTimeout(() => navigate('/marketplace/orders'), 2000);
    } catch (err) {
      setMsg(err.response?.data?.error || '下单失败');
    } finally { setLoading(false); }
  };

  const subtotal = items.reduce((s, i) => s + (i.product?.price || 0), 0);
  const totalShipping = items.reduce((s, i) => s + (shippingInfo[i.product_id]?.shippingCost || 0), 0);
  const total = subtotal + totalShipping;

  return (
    <div className="page-content">
      <div className="discovery-header">
        <h2>🛒 购物车</h2>
        <p className="subtitle">{items.length} 件商品，来自 {new Set(items.map(i => i.product?.country_code)).size} 个国家</p>
      </div>

      {msg && <div className="alert" style={msg.includes('成功')||msg.includes('🎉')?{background:'rgba(74,222,128,0.15)',color:'#4ade80',textAlign:'center',border:'1px solid rgba(74,222,128,0.2)'}:{background:'rgba(255,107,107,0.15)',color:'#ff6b6b',textAlign:'center'}}>{msg}</div>}

      {items.length === 0 ? (
        <div className="page-center" style={{minHeight:'50vh'}}>
          <div style={{textAlign:'center',color:'var(--text-dim)'}}>
            <div style={{fontSize:'4rem',marginBottom:12}}>🛒</div>
            <p>购物车是空的</p>
            <button className="btn btn-primary" style={{marginTop:16}} onClick={() => navigate('/marketplace')}>去逛逛 →</button>
          </div>
        </div>
      ) : (
        <div className="cart-layout">
          <div className="cart-items">
            {items.map(i => {
              const p = i.product;
              if (!p) return null;
              const ship = shippingInfo[p.id];
              return (
                <div key={i.id} className="cart-item">
                  <div className="cart-item-img">{p.image || '📦'}</div>
                  <div className="cart-item-info">
                    <div className="cart-item-title">{p.title}</div>
                    <div className="cart-item-meta" style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      <span className="meta-badge">{FLAGS[p.country_code]||'🌍'} {p.country_code}</span>
                      {p.warehouseName && <span className="meta-badge" style={{background:'rgba(74,222,128,0.1)',color:'#4ade80'}}>🏭 {p.warehouseName}</span>}
                      {p.warehouseCity && <span className="meta-badge">📍 {p.warehouseCity}</span>}
                    </div>
                    {ship && (
                      <div className="cart-item-meta" style={{fontSize:'0.78rem',color:'var(--accent2)'}}>
                        🚚 ${ship.shippingCost} · {ship.estimatedDays}天到达 · {ship.warehouseName}
                      </div>
                    )}
                    <div className="cart-item-price">
                      {CURR_SYM[p.currency]||'$'}{p.price?.toLocaleString?.() || p.price}
                    </div>
                  </div>
                  <button className="btn-icon cart-remove" onClick={() => removeItem(p.id)}>✕</button>
                </div>
              );
            })}
          </div>

          <div className="cart-summary">
            <h3>📋 订单摘要</h3>
            <div className="summary-row">
              <span>商品小计</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>🚚 运费 ({items.length} 件)</span>
              <span style={{color:'var(--accent2)'}}>${totalShipping.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>📦 预计到达</span>
              <span>{Math.max(...items.map(i => shippingInfo[i.product_id]?.estimatedDays||5), 0)}天</span>
            </div>
            <div className="summary-row summary-total">
              <span>合计</span>
              <span>${total.toFixed(2)}</span>
            </div>
            <div className="field" style={{marginTop:16}}>
              <label>📍 收货地址</label>
              <textarea value={address} onChange={e => setAddress(e.target.value)} rows={2} placeholder="例：北京市朝阳区建国路100号, 100022" />
            </div>
            <button className="btn btn-primary btn-block" onClick={checkout} disabled={loading}>
              {loading ? '下单中...' : `💰 支付 $${total.toFixed(2)}`}
            </button>
            <button className="btn btn-secondary btn-block" style={{marginTop:8}} onClick={() => navigate('/marketplace')}>继续购物</button>
          </div>
        </div>
      )}
    </div>
  );
}
